import { useState, useEffect } from "react"
import { Search } from "lucide-react"
import { getUsers } from "@/lib/api"
import { useWorkoutStore } from "@/stores/workoutStore"
import { useDebounce } from "@/hooks/useDebounce"
import { toast } from "sonner"
import type { UserProfile } from "@/types/workout"

export function UserSearch() {
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<UserProfile[]>([])
  const [showDropdown, setShowDropdown] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const { setCurrentUser, loadUserSeason, reset } = useWorkoutStore()

  useEffect(() => {
    async function searchUsers() {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
        setResults([])
        setShowDropdown(false)
        return
      }

      const { currentUser: user } = useWorkoutStore.getState()
      if (
        user &&
        debouncedSearchTerm === `${user.first_name} ${user.last_name}`
      ) {
        setResults([])
        setShowDropdown(false)
        return
      }

      setIsSearching(true)
      try {
        const users = await getUsers(debouncedSearchTerm)
        setResults(users as UserProfile[])
        setShowDropdown(true)
      } catch {
        toast.error("Failed to search users")
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }

    searchUsers()
  }, [debouncedSearchTerm])

  const handleSelectUser = async (user: UserProfile) => {
    const { isDirty } = useWorkoutStore.getState()
    if (isDirty) {
      const ok = window.confirm(
        "You have unsaved workout edits. Switching users will discard them. Continue?",
      )
      if (!ok) return
    }
    setCurrentUser(user)
    setSearchTerm(`${user.first_name} ${user.last_name}`)
    setShowDropdown(false)
    setResults([])
    await loadUserSeason(user.id)
  }

  const handleClear = () => {
    const { isDirty } = useWorkoutStore.getState()
    if (isDirty) {
      const ok = window.confirm(
        "You have unsaved workout edits. Clearing will discard them. Continue?",
      )
      if (!ok) return
    }
    setSearchTerm("")
    setResults([])
    setShowDropdown(false)
    reset()
  }

  return (
    <div className="relative max-w-xl">
      <label
        htmlFor="user-search"
        className="block text-sm font-medium text-muted-foreground mb-2"
      >
        Search for a user
      </label>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="size-5 text-muted-foreground" />
        </div>

        <input
          id="user-search"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            if (searchTerm.length >= 2) setShowDropdown(true)
          }}
          onBlur={() => {
            setTimeout(() => setShowDropdown(false), 200)
          }}
          placeholder="Type name to see suggestions..."
          className="block w-full pl-10 pr-12 py-2 border rounded-md focus:ring-primary focus:border-primary bg-background"
        />

        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
          >
            &times;
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-popover shadow-lg rounded-md border max-h-60 overflow-auto">
          {isSearching ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <ul>
              {results.map((user) => (
                <li key={user.id}>
                  <button
                    onClick={() => handleSelectUser(user)}
                    className="w-full text-left px-4 py-3 hover:bg-accent focus:bg-accent focus:outline-hidden"
                  >
                    <div className="font-medium">
                      {user.first_name} {user.last_name}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No users found matching &ldquo;{debouncedSearchTerm}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  )
}
